<template>
  <main class="mt-3">
    <loading-spinner :visible="isLoading" />

    <div class="container-fluid eis-dashboard">

      <!-- 헤더 + 조회기간 -->
      <div class="eis-header d-flex justify-content-between align-items-end flex-wrap mb-4">
        <div>
          <h2 class="eis-title">EIS 대시보드</h2>
          <p class="eis-subtitle"><i class="bi bi-speedometer2 text-primary fw-bolder fs-6"></i> 일반예약 · 호텔예약 · 외부결제 통합 매출 현황</p>
          <p class="eis-subtitle text-danger fw-bold">※ 일반예약/호텔예약: 확정일 기준, 외부결제: 결제일 기준</p>
        </div>

        <div class="d-flex align-items-center mt-2 mt-md-0">
          <div class="input-group input-group-sm eis-date-group">
            <span class="input-group-text">기간</span>
            <input type="date" class="form-control" v-model="dateStart" ref="dateStart">
            <span class="input-group-text">~</span>
            <input type="date" class="form-control" v-model="dateEnd" ref="dateEnd">
          </div>
          <button type="button" class="btn btn-primary btn-sm ms-2" @click="loadDashboard">
            <i class="bi bi-search"></i>&nbsp;조회
          </button>
          <button type="button" class="btn btn-outline-secondary btn-sm ms-1" @click="setQuickRange(30)">최근30일</button>
          <button type="button" class="btn btn-outline-secondary btn-sm ms-1" @click="setQuickRange('month')">이번달</button>
          <button type="button" class="btn btn-outline-secondary btn-sm ms-1" @click="setQuickRange('year')">올해</button>
        </div>
      </div>

      <!-- KPI 요약카드 -->
      <div class="row g-3 mb-3">
        <div class="col-6 col-lg-3" v-for="(k, i) in kpiCards" :key="i">
          <div class="kpi-card">
            <div class="kpi-label">{{ k.label }}</div>
            <div class="kpi-value" :style="{ color: k.color }">{{ k.value }}</div>
            <div class="kpi-sub">{{ k.sub }}</div>
          </div>
        </div>
      </div>

      <!-- 채널별 요약카드 -->
      <div class="row g-3 mb-4">
        <div class="col-md-4" v-for="(c, key) in channelCards" :key="key">
          <div class="channel-card" :style="{ borderLeftColor: c.color }">
            <div class="channel-card-title">
              <span class="channel-dot" :style="{ backgroundColor: c.color }"></span>
              {{ c.title }}
            </div>
            <div class="channel-card-amt">{{ getCurrencyFormat(c.totalAmt) }}<small>원</small></div>
            <div class="channel-card-row">
              <span>확정건수</span>
              <b>{{ getCurrencyFormat(c.totalCnt) }}건</b>
            </div>
            <div class="channel-card-row">
              <span>취소건수</span>
              <b class="text-danger">{{ getCurrencyFormat(c.cxCnt) }}건</b>
            </div>
            <div class="channel-card-row">
              <span>취소율</span>
              <b :class="getCxRate(c) > 10 ? 'text-danger' : ''">{{ getCxRate(c) }}%</b>
            </div>
          </div>
        </div>
      </div>

      <!-- 차트 1행: 매출추이 + 채널비중 -->
      <div class="row g-3 mb-3">
        <div class="col-lg-8">
          <div class="chart-card">
            <div class="chart-card-header">통합매출추이</div>
            <div class="chart-canvas-wrap">
              <canvas ref="trendChart"></canvas>
            </div>
          </div>
        </div>
        <div class="col-lg-4">
          <div class="chart-card">
            <div class="chart-card-header">채널별 매출 비중</div>
            <div class="chart-canvas-wrap">
              <canvas ref="shareChart"></canvas>
            </div>
          </div>
        </div>
      </div>

      <!-- 차트 2행: 상태분포 + 취소율비교 -->
      <div class="row g-3 mb-4">
        <div class="col-lg-7">
          <div class="chart-card">
            <div class="chart-card-header">채널별 확정/취소 건수</div>
            <div class="chart-canvas-wrap">
              <canvas ref="statusChart"></canvas>
            </div>
          </div>
        </div>
        <div class="col-lg-5">
          <div class="chart-card">
            <div class="chart-card-header">채널별 취소율 비교</div>
            <div class="chart-canvas-wrap">
              <canvas ref="cxRateChart"></canvas>
            </div>
          </div>
        </div>
      </div>

      <div class="mb-4">
        <button type="button" class="btn btn-lg btn-success" @click="goToBack()">돌아가기</button>
      </div>

    </div>
  </main>
</template>

<script>
import Chart from 'chart.js/auto'
import LoadingSpinner from '../components/LoadingSpinner.vue';     // 로딩스피너 컴포넌트

//채널별 시그니처 컬러(카드 강조선/차트 색상에서 동일하게 재사용)
const CH_COLOR = {
  reservation: '#4C6EF5',   //일반예약
  hotel:       '#12B886',   //호텔예약
  external:    '#F59F00',   //외부결제
}
const CX_COLOR = '#FA5252'  //취소(공통)

export default {
  components: {
    LoadingSpinner
  },
  data() {
    return {
      isLoading: false,

      dateStart: "",           //조회시작일
      dateEnd: "",              //조회종료일

      //채널별 요약데이타(백엔드 미연결시에도 화면이 비지 않도록 기본구조 유지)
      reservationSummary: { totalAmt: 0, totalCnt: 0, cxCnt: 0, cxAmt: 0, trend: [] },
      hotelSummary:       { totalAmt: 0, totalCnt: 0, cxCnt: 0, cxAmt: 0, trend: [] },
      externalSummary:    { totalAmt: 0, totalCnt: 0, cxCnt: 0, cxAmt: 0, trend: [] },

      //chart.js 인스턴스 보관(재조회 시 destroy 후 재생성)
      charts: {
        trend: null,
        share: null,
        status: null,
        cxRate: null,
      },
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },

    combinedTotalAmt() {
      return this.reservationSummary.totalAmt + this.hotelSummary.totalAmt + this.externalSummary.totalAmt
    },
    combinedTotalCnt() {
      return this.reservationSummary.totalCnt + this.hotelSummary.totalCnt + this.externalSummary.totalCnt
    },
    combinedCxCnt() {
      return this.reservationSummary.cxCnt + this.hotelSummary.cxCnt + this.externalSummary.cxCnt
    },
    combinedCxRate() {
      let base = this.combinedTotalCnt + this.combinedCxCnt
      return base > 0 ? ((this.combinedCxCnt / base) * 100).toFixed(1) : "0.0"
    },
    topChannel() {
      let arr = [
        { name: "일반예약", amt: this.reservationSummary.totalAmt },
        { name: "호텔예약", amt: this.hotelSummary.totalAmt },
        { name: "외부결제", amt: this.externalSummary.totalAmt },
      ]
      arr.sort((a, b) => b.amt - a.amt)
      return arr[0]
    },

    kpiCards() {
      return [
        {
          label: "통합 총매출액",
          value: this.getCurrencyFormat(this.combinedTotalAmt) + "원",
          sub: `${this.dateStart} ~ ${this.dateEnd}`,
          color: "#212529"
        },
        {
          label: "통합 확정건수",
          value: this.getCurrencyFormat(this.combinedTotalCnt) + "건",
          sub: "확정(CO) 기준",
          color: "#212529"
        },
        {
          label: "통합 취소율",
          value: this.combinedCxRate + "%",
          sub: `취소 ${this.getCurrencyFormat(this.combinedCxCnt)}건`,
          color: this.combinedCxRate > 10 ? CX_COLOR : "#212529"
        },
        {
          label: "최고매출 채널",
          value: this.topChannel.name,
          sub: this.getCurrencyFormat(this.topChannel.amt) + "원",
          color: "#212529"
        },
      ]
    },

    channelCards() {
      return {
        reservation: { title: "일반예약", color: CH_COLOR.reservation, ...this.reservationSummary },
        hotel:       { title: "호텔예약", color: CH_COLOR.hotel, ...this.hotelSummary },
        external:    { title: "외부결제", color: CH_COLOR.external, ...this.externalSummary },
      }
    },
  },
  created() {
    //기본조회기간: 당해년 1/1 ~ 오늘
    this.setQuickRange('year')
    // //기본조회기간: 최근30일
    // this.setQuickRange(30)
    // //기본조회기간: 조회월 1일 ~ 오늘
    // this.setQuickRange('month')
  },
  mounted() {
  if(this.user.handlerGrade > 4 || this.user.handlerGrade == undefined) {
    var swal = require('sweetalert2');
    swal.fire({
      title: '',
      text: '로그인 권한이 없습니다.',
      icon: 'error'
    });
    this.$router.push({path: '/b2bLogin'});   // ← 이 줄 추가
    return;                            // ← 이후 로직(loadDashboard 등) 실행 방지
  }

  document.getElementById("user").style.display = "none";

  this.loadDashboard();
},
  beforeUnmount() {
    //차트인스턴스 정리(메모리누수 방지)
    Object.keys(this.charts).forEach(key => {
      if(this.charts[key]) {
        this.charts[key].destroy()
        this.charts[key] = null
      }
    })
  },
  methods:
  {
    //mixins 공통함수
    getDateFormat(date)
    {
      return this.$dateFormat(date);
    },
    getCurrencyFormat(value)
    {
      return this.$currencyFormat(value);
    },

    //취소율(%) 계산(공통)
    getCxRate(summary)
    {
      let base = summary.totalCnt + summary.cxCnt
      return base > 0 ? ((summary.cxCnt / base) * 100).toFixed(1) : "0.0"
    },

    //빠른기간설정
    setQuickRange(param)
    {
      let today = new Date();
      let start = new Date();

      if(param === "month")
      {
        start = new Date(today.getFullYear(), today.getMonth(), 1)
      }
      else if(param === "year")
      {
        start = new Date(today.getFullYear(), 0, 1)   // 당해년 1월 1일
      }
      else
      {
        start.setDate(today.getDate() - (param - 1))
      }

      this.dateEnd = this.toDateInput(today)
      this.dateStart = this.toDateInput(start)
    },

    //Date -> yyyy-mm-dd
    toDateInput(d)
    {
      let yyyy = d.getFullYear();
      let mm = String(d.getMonth() + 1).padStart(2, '0');
      let dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },

    //대시보드 전체 데이타 로드(3채널 병렬조회)
    async loadDashboard()
    {
      if(!this.dateStart || !this.dateEnd)
      {
        this.$swal.fire("","조회기간을 입력하세요","warning")
        return false;
      }
      if(this.dateStart > this.dateEnd)
      {
        this.$swal.fire("","시작일은 종료일보다 후일일 수 없습니다","warning")
        return false;
      }

      this.isLoading = true;

      try
      {
        //TODO(백엔드): 아래 3개 alias를 sql.js에 신규 등록 필요. 응답 스펙은 하단 normalizeSummary 주석 참고
        let [resSum, hotelSum, extSum] = await Promise.all([
          this.$api("/api/getEisReservationSummary", { param: [this.dateStart, this.dateEnd] }),
          this.$api("/api/getEisHotelSummary",       { param: [this.dateStart, this.dateEnd] }),
          this.$api("/api/getEisExternalPaySummary", { param: [this.dateStart, this.dateEnd] }),
        ])

        this.reservationSummary = this.buildSummary(resSum)
        this.hotelSummary       = this.buildSummary(hotelSum)
        this.externalSummary    = this.buildSummary(extSum)

        this.$nextTick(() => {
          this.renderCharts()
        })
      }
      catch(err)
      {
        console.error("loadDashboard error:", err)
        this.$swal.fire("","대시보드 데이타 조회 중 오류가 발생했습니다.","error")
      }
      finally
      {
        this.isLoading = false;
      }
    },

    // API 응답(일자별 집계행 배열)을 화면에서 쓰는 요약구조로 변환
    // 기대하는 응답형태: [ { eisDate:'2026-07-01', amt:450000, cnt:3, cxAmt:0, cxCnt:0 }, ... ]
    buildSummary(rows)
    {
      let base = { totalAmt: 0, totalCnt: 0, cxCnt: 0, cxAmt: 0, trend: [] }
      if(!Array.isArray(rows)) return base

      rows.forEach(r => {
        base.totalAmt += Number(r.amt) || 0
        base.totalCnt += Number(r.cnt) || 0
        base.cxAmt    += Number(r.cxAmt) || 0
        base.cxCnt    += Number(r.cxCnt) || 0
        //base.trend.push({ date: r.eisDate, amt: Number(r.amt) || 0 })
        base.trend.push({ date: this.getDateFormat(r.eisDate), amt: Number(r.amt) || 0 })
      })
      return base
    },

    normalizeSummary(res)
    {
      let base = { totalAmt: 0, totalCnt: 0, cxCnt: 0, cxAmt: 0, trend: [] }
      if(!res) return base

      return {
        totalAmt: Number(res.totalAmt) || 0,
        totalCnt: Number(res.totalCnt) || 0,
        cxCnt:    Number(res.cxCnt) || 0,
        cxAmt:    Number(res.cxAmt) || 0,
        trend:    Array.isArray(res.trend) ? res.trend : [],
      }
    },

    //차트전체 재렌더링
    renderCharts()
    {
      this.renderTrendChart()
      this.renderShareChart()
      this.renderStatusChart()
      this.renderCxRateChart()
    },

    //1) 통합 매출 추이(라인차트, 채널별 3계열)
    renderTrendChart()
    {
      if(this.charts.trend) this.charts.trend.destroy()

      //3채널의 날짜를 합쳐서 x축 라벨 구성
      let dateSet = new Set()
      ;[this.reservationSummary, this.hotelSummary, this.externalSummary].forEach(s => {
        s.trend.forEach(t => dateSet.add(t.date))
      })
      let labels = Array.from(dateSet).sort()

      let toSeries = (summary) => labels.map(d => {
        let found = summary.trend.find(t => t.date === d)
        return found ? found.amt : 0
      })

      let ctx = this.$refs.trendChart.getContext('2d')
      this.charts.trend = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [
            {
              label: '일반예약', data: toSeries(this.reservationSummary),
              borderColor: CH_COLOR.reservation, backgroundColor: CH_COLOR.reservation + '22',
              tension: 0.3, fill: true, pointRadius: 2,
            },
            {
              label: '호텔예약', data: toSeries(this.hotelSummary),
              borderColor: CH_COLOR.hotel, backgroundColor: CH_COLOR.hotel + '22',
              tension: 0.3, fill: true, pointRadius: 2,
            },
            {
              label: '외부결제', data: toSeries(this.externalSummary),
              borderColor: CH_COLOR.external, backgroundColor: CH_COLOR.external + '22',
              tension: 0.3, fill: true, pointRadius: 2,
            },
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          scales: {
            y: {
              ticks: {
                callback: (v) => this.getCurrencyFormat(v)
              }
            }
          },
          plugins: {
            legend: { position: 'bottom' },
            tooltip: {
              callbacks: {
                label: (c) => `${c.dataset.label}: ${this.getCurrencyFormat(c.raw)}원`
              }
            }
          }
        }
      })
    },

    //2) 채널별 매출비중(도넛차트)
    renderShareChart()
    {
      if(this.charts.share) this.charts.share.destroy()

      let ctx = this.$refs.shareChart.getContext('2d')
      this.charts.share = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['일반예약', '호텔예약', '외부결제'],
          datasets: [{
            data: [this.reservationSummary.totalAmt, this.hotelSummary.totalAmt, this.externalSummary.totalAmt],
            backgroundColor: [CH_COLOR.reservation, CH_COLOR.hotel, CH_COLOR.external],
            borderWidth: 0,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '65%',
          plugins: {
            legend: { position: 'bottom' },
            tooltip: {
              callbacks: {
                label: (c) => {
                  let total = c.dataset.data.reduce((a, b) => a + b, 0)
                  let pct = total > 0 ? ((c.raw / total) * 100).toFixed(1) : 0
                  return `${c.label}: ${this.getCurrencyFormat(c.raw)}원 (${pct}%)`
                }
              }
            }
          }
        }
      })
    },

    //3) 채널별 확정/취소 건수(누적 막대차트)
    renderStatusChart()
    {
      if(this.charts.status) this.charts.status.destroy()

      let ctx = this.$refs.statusChart.getContext('2d')
      this.charts.status = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: ['일반예약', '호텔예약', '외부결제'],
          datasets: [
            {
              label: '확정건수',
              data: [this.reservationSummary.totalCnt, this.hotelSummary.totalCnt, this.externalSummary.totalCnt],
              backgroundColor: '#4C6EF5',
              borderRadius: 4,
            },
            {
              label: '취소건수',
              data: [this.reservationSummary.cxCnt, this.hotelSummary.cxCnt, this.externalSummary.cxCnt],
              backgroundColor: CX_COLOR,
              borderRadius: 4,
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: { stacked: true },
            y: { stacked: true, ticks: { precision: 0 } }
          },
          plugins: { legend: { position: 'bottom' } }
        }
      })
    },

    //4) 채널별 취소율 비교(가로 막대차트)
    renderCxRateChart()
    {
      if(this.charts.cxRate) this.charts.cxRate.destroy()

      let ctx = this.$refs.cxRateChart.getContext('2d')
      this.charts.cxRate = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: ['일반예약', '호텔예약', '외부결제'],
          datasets: [{
            label: '취소율(%)',
            data: [
              Number(this.getCxRate(this.reservationSummary)),
              Number(this.getCxRate(this.hotelSummary)),
              Number(this.getCxRate(this.externalSummary)),
            ],
            backgroundColor: [CH_COLOR.reservation, CH_COLOR.hotel, CH_COLOR.external],
            borderRadius: 4,
          }]
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: { ticks: { callback: (v) => v + '%' } }
          },
          plugins: { legend: { display: false } }
        }
      })
    },

    goToBack()
    {
      history.back();
    },
  }
}
</script>

<style scoped>
.eis-dashboard {
  --eis-border: #e9ecef;
  --eis-muted: #868e96;
}

.eis-title {
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-bottom: 2px;
}
.eis-subtitle {
  color: var(--eis-muted);
  font-size: 0.85rem;
  margin-bottom: 0;
}
.eis-date-group {
  width: auto;
}
.eis-date-group input[type="date"] {
  max-width: 145px;
}

/**KPI 카드 */
.kpi-card {
  background: #fff;
  border: 1px solid var(--eis-border);
  border-radius: 10px;
  padding: 16px 18px;
  height: 100%;
}
.kpi-label {
  font-size: 0.78rem;
  color: var(--eis-muted);
  margin-bottom: 6px;
}
.kpi-value {
  font-size: 1.55rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}
.kpi-sub {
  font-size: 0.75rem;
  color: #adb5bd;
  margin-top: 4px;
}

/**채널 요약카드 */
.channel-card {
  background: #fff;
  border: 1px solid var(--eis-border);
  border-left-width: 5px;
  border-left-style: solid;
  border-radius: 10px;
  padding: 16px 18px;
  height: 100%;
}
.channel-card-title {
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}
.channel-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 7px;
}
.channel-card-amt {
  font-size: 1.35rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  margin-bottom: 10px;
}
.channel-card-amt small {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--eis-muted);
  margin-left: 2px;
}
.channel-card-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  padding: 3px 0;
  color: #495057;
}

/**차트 카드 */
.chart-card {
  background: #fff;
  border: 1px solid var(--eis-border);
  border-radius: 10px;
  padding: 16px 18px;
  height: 100%;
}
.chart-card-header {
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 12px;
  color: #343a40;
}
.chart-canvas-wrap {
  position: relative;
  height: 300px;
}
</style>